import { CopyId } from "@/components/copy-id";
import { SiteHeader } from "@/components/site-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  type BadgeDto,
  type BadgeVariant,
  type UpdateBadgePayload,
  badgesProvider,
} from "@/providers/badges.provider";
import {
  IconAlertTriangle,
  IconChevronLeft,
  IconChevronRight,
  IconLoader2,
  IconMedal,
  IconPhoto,
  IconPlus,
  IconSparkles,
  IconTrash,
  IconUpload,
  IconUserMinus,
  IconUserPlus,
  IconX,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

const PAGE_SIZE = 50;

// ── Helpers ───────────────────────────────────────────────────────────────────

function VisibilityBadge({ isVisible }: { isVisible: boolean }) {
  return isVisible ? (
    <Badge variant="outline" className="border-green-500 text-green-600">
      Visible
    </Badge>
  ) : (
    <Badge variant="secondary">Hidden</Badge>
  );
}

function AvailabilityBadge({
  availableFrom,
  availableTo,
}: {
  availableFrom: string | null;
  availableTo: string | null;
}) {
  const now = Date.now();
  const from = availableFrom ? new Date(availableFrom).getTime() : null;
  const to = availableTo ? new Date(availableTo).getTime() : null;

  if (from !== null && now < from) {
    return <Badge variant="secondary">Upcoming</Badge>;
  }
  if (to !== null && now > to) {
    return (
      <Badge variant="outline" className="border-orange-500 text-orange-600">
        Expired
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="border-blue-500 text-blue-600">
      Active
    </Badge>
  );
}

function BadgeRow({
  badge,
  onClick,
}: {
  badge: BadgeDto;
  onClick: () => void;
}) {
  const availableFrom = badge.available_from
    ? new Date(badge.available_from).toLocaleDateString()
    : "—";
  const availableTo = badge.available_to
    ? new Date(badge.available_to).toLocaleDateString()
    : "—";

  const { data: variants } = useQuery({
    queryKey: ["badge-variants", badge.badge_id],
    queryFn: () => badgesProvider.getVariantsForBadge(badge.badge_id),
    staleTime: 60_000,
  });

  return (
    <TableRow className="cursor-pointer" onClick={onClick}>
      <TableCell>
        <CopyId id={badge.badge_id} />
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-3">
          {badge.img_url ? (
            <img
              src={badge.img_url}
              alt={badge.name}
              className="size-8 rounded object-cover border"
            />
          ) : (
            <div className="size-8 rounded border bg-muted flex items-center justify-center">
              <IconMedal className="size-4 text-muted-foreground" />
            </div>
          )}
          <span className="font-medium">{badge.name}</span>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-1">
          {variants
            ? variants.map((v) => (
                <Badge
                  key={v.variant_id}
                  variant={v.variant_name === "standard" ? "secondary" : "outline"}
                  className="text-[10px] px-1.5 py-0 font-mono"
                >
                  {v.variant_name}
                </Badge>
              ))
            : <Skeleton className="h-4 w-16" />}
        </div>
      </TableCell>
      <TableCell className="text-muted-foreground text-xs font-mono">
        {badge.action_type}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {badge.action_threshold.toLocaleString()}
      </TableCell>
      <TableCell>
        <AvailabilityBadge
          availableFrom={badge.available_from}
          availableTo={badge.available_to}
        />
      </TableCell>
      <TableCell>
        <VisibilityBadge isVisible={badge.is_visible} />
      </TableCell>
      <TableCell className="text-muted-foreground text-xs">
        {availableFrom} – {availableTo}
      </TableCell>
    </TableRow>
  );
}

// ── Image helpers ─────────────────────────────────────────────────────────────

function handleImageFile(
  file: File,
  onSuccess: (base64: string) => void,
  onError: (msg: string) => void,
) {
  if (!["image/jpeg", "image/png"].includes(file.type)) {
    onError("Only JPEG and PNG images are supported");
    return;
  }
  if (file.size > 500 * 1024) {
    onError("Image must be under 500 KB");
    return;
  }
  const reader = new FileReader();
  reader.onload = () => {
    const base64 = (reader.result as string).split(",")[1];
    onSuccess(base64);
  };
  reader.readAsDataURL(file);
}

// ── Create badge sheet ────────────────────────────────────────────────────────

function CreateBadgeSheet({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState("");
  const [actionType, setActionType] = useState("");
  const [actionThreshold, setActionThreshold] = useState("");
  const [actionDescription, setActionDescription] = useState("");
  const [isVisible, setIsVisible] = useState(false);
  const [availableFrom, setAvailableFrom] = useState("");
  const [availableTo, setAvailableTo] = useState("");
  const [imageFile, setImageFile] = useState<{
    file: File;
    base64: string;
  } | null>(null);

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      badgesProvider.create({
        name: name.trim(),
        img_content: imageFile?.base64,
        is_visible: isVisible,
        available_from: availableFrom
          ? new Date(availableFrom).toISOString()
          : null,
        available_to: availableTo ? new Date(availableTo).toISOString() : null,
        action_type: actionType.trim(),
        action_threshold: parseInt(actionThreshold, 10),
        action_description: actionDescription.trim(),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["badges"] });
      toast.success("Badge created");
      resetAndClose();
    },
    onError: (err) => toast.error(err.message),
  });

  function resetAndClose() {
    setName("");
    setActionType("");
    setActionThreshold("");
    setActionDescription("");
    setIsVisible(false);
    setAvailableFrom("");
    setAvailableTo("");
    setImageFile(null);
    onClose();
  }

  function handleClose() {
    if (!isPending) resetAndClose();
  }

  const canSubmit =
    name.trim() &&
    actionType.trim() &&
    actionDescription.trim() &&
    actionThreshold !== "" &&
    !isNaN(parseInt(actionThreshold, 10));

  return (
    <Sheet open={open} onOpenChange={(o) => !o && handleClose()}>
      <SheetContent className="sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>New Badge</SheetTitle>
          <SheetDescription>
            Create a new badge users can earn.
          </SheetDescription>
        </SheetHeader>

        <div className="px-4 flex flex-col gap-4">
          {/* Image */}
          <div className="flex flex-col gap-1.5">
            <Label>Image</Label>
            <div className="flex items-center gap-3">
              <div
                className="size-16 rounded-lg border bg-muted flex items-center justify-center overflow-hidden shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => fileRef.current?.click()}
              >
                {imageFile ? (
                  <img
                    src={URL.createObjectURL(imageFile.file)}
                    alt="preview"
                    className="size-full object-cover"
                  />
                ) : (
                  <IconMedal className="size-6 text-muted-foreground" />
                )}
              </div>
              <div className="flex flex-col gap-1.5">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileRef.current?.click()}
                  className="gap-1.5"
                >
                  <IconUpload className="size-3.5" />
                  {imageFile ? "Change image" : "Choose image"}
                </Button>
                {imageFile && (
                  <button
                    type="button"
                    className="text-xs text-muted-foreground hover:text-destructive text-left"
                    onClick={() => setImageFile(null)}
                  >
                    Remove
                  </button>
                )}
                <p className="text-xs text-muted-foreground">
                  JPEG or PNG, max 500 KB
                </p>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  handleImageFile(
                    file,
                    (base64) => setImageFile({ file, base64 }),
                    (msg) => toast.error(msg),
                  );
                  e.target.value = "";
                }}
              />
            </div>
          </div>

          {/* Name */}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-badge-name">Name</Label>
            <Input
              id="new-badge-name"
              placeholder="e.g. Early Adopter"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          {/* Action type */}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-badge-action-type">Action type</Label>
            <Input
              id="new-badge-action-type"
              placeholder="e.g. distance_traveled"
              value={actionType}
              onChange={(e) => setActionType(e.target.value)}
              className="font-mono text-sm"
            />
          </div>

          {/* Threshold */}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-badge-threshold">Action threshold</Label>
            <Input
              id="new-badge-threshold"
              type="number"
              min={0}
              placeholder="e.g. 1000"
              value={actionThreshold}
              onChange={(e) => setActionThreshold(e.target.value)}
            />
          </div>

          {/* Action description */}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-badge-action-desc">Action description</Label>
            <Textarea
              id="new-badge-action-desc"
              placeholder="e.g. Travel 1,000 meters"
              value={actionDescription}
              onChange={(e) => setActionDescription(e.target.value)}
              rows={2}
            />
          </div>

          {/* Availability dates */}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-badge-from">Available from (optional)</Label>
            <Input
              id="new-badge-from"
              type="datetime-local"
              value={availableFrom}
              onChange={(e) => setAvailableFrom(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-badge-to">Available to (optional)</Label>
            <Input
              id="new-badge-to"
              type="datetime-local"
              value={availableTo}
              onChange={(e) => setAvailableTo(e.target.value)}
            />
          </div>

          {/* Visible */}
          <div className="flex items-center justify-between">
            <Label htmlFor="new-badge-visible">Visible to users</Label>
            <Switch
              id="new-badge-visible"
              checked={isVisible}
              onCheckedChange={setIsVisible}
            />
          </div>
        </div>

        <SheetFooter className="px-4">
          <Button variant="outline" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={() => mutate()} disabled={isPending || !canSubmit}>
            {isPending ? (
              <IconLoader2 className="size-4 animate-spin" />
            ) : (
              <IconPlus className="size-4" />
            )}
            Create
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

// ── Filter preview canvas ─────────────────────────────────────────────────────

export function BadgeFilterPreviewCanvas({
  baseImageUrl,
  filterImageUrl,
  opacity = 0.5,
  effect = "none",
  size = 400,
}: {
  baseImageUrl: string;
  filterImageUrl: string;
  opacity?: number;
  effect?: string;
  size?: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const baseImg = new Image();
    const filterImg = new Image();
    baseImg.crossOrigin = "anonymous";
    filterImg.crossOrigin = "anonymous";

    let animationFrameId: number;
    const sparkles: { x: number; y: number; size: number; phase: number; speed: number }[] = [];

    if (effect === "sparkles") {
      for (let i = 0; i < 20; i++) {
        sparkles.push({
          x: Math.random() * size,
          y: Math.random() * size,
          size: (size / 400) * (4 + Math.random() * 6),
          phase: Math.random() * Math.PI * 2,
          speed: 0.02 + Math.random() * 0.04,
        });
      }
    }

    Promise.all([
      new Promise<void>((resolve) => { baseImg.onload = () => resolve(); baseImg.src = baseImageUrl; }),
      new Promise<void>((resolve) => { filterImg.onload = () => resolve(); filterImg.src = filterImageUrl; }),
    ]).then(() => {
      // Create an offscreen canvas to hold the blended image
      const offscreen = document.createElement("canvas");
      offscreen.width = size;
      offscreen.height = size;
      const offCtx = offscreen.getContext("2d")!;
      
      offCtx.drawImage(baseImg, 0, 0, size, size);
      let baseImageData: ImageData;
      try {
        baseImageData = offCtx.getImageData(0, 0, size, size);
        const basePixels = baseImageData.data;

        const tempCanvas = document.createElement("canvas");
        tempCanvas.width = size;
        tempCanvas.height = size;
        const tempCtx = tempCanvas.getContext("2d")!;
        tempCtx.drawImage(filterImg, 0, 0, size, size);
        const filterPixels = tempCtx.getImageData(0, 0, size, size).data;

        for (let i = 0; i < basePixels.length; i += 4) {
          const r1 = basePixels[i], g1 = basePixels[i + 1], b1 = basePixels[i + 2];
          const r2 = filterPixels[i], g2 = filterPixels[i + 1], b2 = filterPixels[i + 2];
          const lumBase = 0.299 * r1 + 0.587 * g1 + 0.114 * b1;
          const lumFilter = 0.299 * r2 + 0.587 * g2 + 0.114 * b2;
          if (lumFilter > lumBase) {
            basePixels[i]     = r2 * opacity + r1 * (1 - opacity);
            basePixels[i + 1] = g2 * opacity + g1 * (1 - opacity);
            basePixels[i + 2] = b2 * opacity + b1 * (1 - opacity);
          }
        }
        offCtx.putImageData(baseImageData, 0, 0);
      } catch {
        offCtx.drawImage(filterImg, 0, 0, size, size);
      }

      const animate = () => {
        ctx.clearRect(0, 0, size, size);
        ctx.drawImage(offscreen, 0, 0);

        if (effect === "sparkles") {
          sparkles.forEach((s) => {
            s.phase += s.speed;
            const sOpacity = (Math.sin(s.phase) + 1) / 2;
            const r = s.size;
            
            ctx.beginPath();
            ctx.moveTo(s.x - r, s.y);
            ctx.lineTo(s.x + r, s.y);
            ctx.moveTo(s.x, s.y - r);
            ctx.lineTo(s.x, s.y + r);
            ctx.strokeStyle = `rgba(255, 255, 255, ${sOpacity * 0.9})`;
            ctx.lineWidth = Math.max(0.5, (size / 400) * 1.5);
            ctx.stroke();

            ctx.beginPath();
            ctx.arc(s.x, s.y, Math.max(0.2, (size / 400) * 1), 0, Math.PI * 2);
            ctx.fillStyle = "white";
            ctx.fill();
          });
          animationFrameId = requestAnimationFrame(animate);
        }
      };

      if (effect === "sparkles") {
        animate();
      } else {
        ctx.clearRect(0, 0, size, size);
        ctx.drawImage(offscreen, 0, 0);
      }
    });

    return () => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
  }, [baseImageUrl, filterImageUrl, opacity, effect, size]);

  return (
    <canvas
      ref={canvasRef}
      width={size}
      height={size}
      className="rounded object-cover border shrink-0"
      style={{ width: size, height: size }}
    />
  );
}

// ── Badge detail sheet ────────────────────────────────────────────────────────

function toDatetimeLocal(iso: string | null | undefined): string {
  if (!iso) return "";
  return iso.slice(0, 16);
}

function VariantRow({
  variant,
  badgeId,
  confirmDeleteId,
  onConfirmDelete,
  deleteMut,
}: {
  variant: BadgeVariant;
  badgeId: string;
  confirmDeleteId: string | null;
  onConfirmDelete: (id: string | null) => void;
  deleteMut: ReturnType<typeof useMutation<void, Error, string>>;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [editDesc, setEditDesc] = useState(variant.variant_description ?? "");

  const updateMut = useMutation({
    mutationFn: (desc: string | null) =>
      badgesProvider.updateBadgeVariant(badgeId, variant.variant_id, desc),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["badge-variants", badgeId] });
      toast.success("Description updated");
      setEditing(false);
    },
    onError: (err) => toast.error(err.message),
  });

  return (
    <div className="flex items-center gap-2 px-3 py-2">
      <span className="font-mono shrink-0">{variant.variant_name}</span>
      <div className="flex-1 min-w-0">
        {editing ? (
          <div className="flex items-center gap-1">
            <Input
              value={editDesc}
              onChange={(e) => setEditDesc(e.target.value)}
              className="h-6 text-xs py-0"
              autoFocus
              disabled={updateMut.isPending}
            />
            <button
              className="text-primary hover:underline shrink-0"
              onClick={() =>
                updateMut.mutate(editDesc.trim() || null)
              }
              disabled={updateMut.isPending}
            >
              {updateMut.isPending ? (
                <IconLoader2 className="size-3 animate-spin" />
              ) : (
                "Save"
              )}
            </button>
            <button
              className="text-muted-foreground hover:underline shrink-0"
              onClick={() => {
                setEditDesc(variant.variant_description ?? "");
                setEditing(false);
              }}
              disabled={updateMut.isPending}
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            className="text-muted-foreground hover:text-foreground truncate max-w-full text-left"
            onClick={() => setEditing(true)}
            title="Edit description"
          >
            {variant.variant_description ?? (
              <span className="italic">No description</span>
            )}
          </button>
        )}
      </div>
      {variant.variant_name !== "standard" && (
        <>
          {confirmDeleteId === variant.variant_id ? (
            <div className="flex items-center gap-1 shrink-0">
              <button
                className="text-destructive hover:underline"
                onClick={() => deleteMut.mutate(variant.variant_id)}
                disabled={deleteMut.isPending}
              >
                {deleteMut.isPending ? (
                  <IconLoader2 className="size-3 animate-spin" />
                ) : (
                  "Confirm"
                )}
              </button>
              <button
                className="text-muted-foreground hover:underline"
                onClick={() => onConfirmDelete(null)}
                disabled={deleteMut.isPending}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              className="text-muted-foreground hover:text-destructive transition-colors shrink-0"
              onClick={() => onConfirmDelete(variant.variant_id)}
              title="Remove variant"
            >
              <IconX className="size-3" />
            </button>
          )}
        </>
      )}
    </div>
  );
}

function BadgeVariantsSection({ badge }: { badge: BadgeDto }) {
  const qc = useQueryClient();
  const [addVariantName, setAddVariantName] = useState("");
  const [addVariantDescription, setAddVariantDescription] = useState("");
  const [confirmDeleteVariantId, setConfirmDeleteVariantId] = useState<
    string | null
  >(null);

  const { data: variants, isLoading: variantsLoading } = useQuery({
    queryKey: ["badge-variants", badge.badge_id],
    queryFn: () => badgesProvider.getVariantsForBadge(badge.badge_id),
  });

  const { data: variantTypes, isLoading: typesLoading } = useQuery({
    queryKey: ["badge-variant-types"],
    queryFn: () => badgesProvider.listVariantTypes(),
    staleTime: 5 * 60_000,
  });

  const existingNames = new Set(variants?.map((v) => v.variant_name) ?? []);
  const availableTypes =
    variantTypes?.filter((t) => !existingNames.has(t.name)) ?? [];

  const addMut = useMutation({
    mutationFn: () =>
      badgesProvider.addVariantToBadge(badge.badge_id, {
        variant_name: addVariantName,
        variant_description: addVariantDescription.trim() || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["badge-variants", badge.badge_id] });
      toast.success("Variant added");
      setAddVariantName("");
      setAddVariantDescription("");
    },
    onError: (err) => toast.error(err.message),
  });

  const deleteMut = useMutation({
    mutationFn: (variantId: string) =>
      badgesProvider.deleteVariantFromBadge(badge.badge_id, variantId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["badge-variants", badge.badge_id] });
      toast.success("Variant removed");
      setConfirmDeleteVariantId(null);
    },
    onError: (err) => {
      toast.error(err.message);
      setConfirmDeleteVariantId(null);
    },
  });

  return (
    <div className="px-4 flex flex-col gap-3">
      <h3 className="font-semibold text-sm">Variants</h3>

      {variantsLoading ? (
        <div className="flex flex-col gap-1.5">
          {Array.from({ length: 2 }).map((_, i) => (
            <Skeleton key={i} className="h-8 w-full" />
          ))}
        </div>
      ) : (
        <div className="flex flex-col divide-y rounded-md border text-xs">
          {!variants?.length ? (
            <p className="px-3 py-2 text-muted-foreground">No variants yet.</p>
          ) : (
            variants.map((variant) => (
              <VariantRow
                key={variant.variant_id}
                variant={variant}
                badgeId={badge.badge_id}
                confirmDeleteId={confirmDeleteVariantId}
                onConfirmDelete={setConfirmDeleteVariantId}
                deleteMut={deleteMut}
              />
            ))
          )}
        </div>
      )}

      {availableTypes.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <Select
              value={addVariantName}
              onValueChange={setAddVariantName}
              disabled={typesLoading || addMut.isPending}
            >
              <SelectTrigger className="flex-1 text-xs h-8">
                <SelectValue placeholder="Select variant type" />
              </SelectTrigger>
              <SelectContent>
                {availableTypes.map((t) => (
                  <SelectItem key={t.name} value={t.name} className="text-xs font-mono">
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              disabled={!addVariantName || addMut.isPending}
              onClick={() => addMut.mutate()}
              className="shrink-0 gap-1.5"
            >
              {addMut.isPending ? (
                <IconLoader2 className="size-3.5 animate-spin" />
              ) : (
                <IconPlus className="size-3.5" />
              )}
              Add
            </Button>
          </div>
          <Input
            placeholder="Description (optional)"
            value={addVariantDescription}
            onChange={(e) => setAddVariantDescription(e.target.value)}
            disabled={!addVariantName || addMut.isPending}
            className="text-xs h-8"
          />
        </div>
      )}
    </div>
  );
}

function BadgeDetailSheet({
  badge,
  onClose,
}: {
  badge: BadgeDto | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);

  const [editName, setEditName] = useState("");
  const [editActionType, setEditActionType] = useState("");
  const [editActionThreshold, setEditActionThreshold] = useState("");
  const [editActionDescription, setEditActionDescription] = useState("");
  const [editIsVisible, setEditIsVisible] = useState(false);
  const [editAvailableFrom, setEditAvailableFrom] = useState("");
  const [editAvailableTo, setEditAvailableTo] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [grantUserId, setGrantUserId] = useState("");
  const [grantVariantName, setGrantVariantName] = useState("standard");
  const [revokeUserId, setRevokeUserId] = useState("");
  const [revokeVariantName, setRevokeVariantName] = useState("standard");
  const [previewVariantName, setPreviewVariantName] = useState("");
  const [previewOpacity, setPreviewOpacity] = useState(50);
  const [previewEffect, setPreviewEffect] = useState("none");

  // Sync edit state when badge changes
  const prevBadgeId = useRef<string | null>(null);
  if (badge && badge.badge_id !== prevBadgeId.current) {
    prevBadgeId.current = badge.badge_id;
    setEditName(badge.name);
    setEditActionType(badge.action_type);
    setEditActionThreshold(String(badge.action_threshold));
    setEditActionDescription(badge.action_description);
    setEditIsVisible(badge.is_visible);
    setEditAvailableFrom(toDatetimeLocal(badge.available_from));
    setEditAvailableTo(toDatetimeLocal(badge.available_to));
    setConfirmDelete(false);
    setGrantUserId("");
    setGrantVariantName("standard");
    setRevokeUserId("");
    setRevokeVariantName("standard");
    setPreviewVariantName("");
    setPreviewOpacity(50);
    setPreviewEffect("none");
  }

  const { data: variants } = useQuery({
    queryKey: ["badge-variants", badge?.badge_id],
    queryFn: () => badgesProvider.getVariantsForBadge(badge!.badge_id),
    enabled: !!badge,
  });

  const { data: variantTypes } = useQuery({
    queryKey: ["badge-variant-types"],
    queryFn: () => badgesProvider.listVariantTypes(),
    staleTime: 5 * 60_000,
  });

  const filterUrlByName = Object.fromEntries(
    (variantTypes ?? []).map((vt) => [vt.name, vt.filter_img_url])
  );

  const variantsWithFilter = (variants ?? []).filter(
    (v) => filterUrlByName[v.variant_name]
  );

  const updateMut = useMutation({
    mutationFn: (payload: UpdateBadgePayload) =>
      badgesProvider.update(badge!.badge_id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["badges"] });
      toast.success("Badge updated");
    },
    onError: (err) => toast.error(err.message),
  });

  const uploadMut = useMutation({
    mutationFn: (base64: string) =>
      badgesProvider.uploadImage(badge!.badge_id, base64),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["badges"] });
      toast.success("Image uploaded");
    },
    onError: (err) => toast.error(err.message),
  });

  const deleteMut = useMutation({
    mutationFn: () => badgesProvider.delete(badge!.badge_id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["badges"] });
      toast.success("Badge deleted");
      onClose();
    },
    onError: (err) => toast.error(err.message),
  });

  const grantMut = useMutation({
    mutationFn: ({ userId, variantName }: { userId: string; variantName: string }) =>
      badgesProvider.grantToUser(badge!.badge_id, userId, variantName),
    onSuccess: () => {
      toast.success("Badge granted");
      setGrantUserId("");
    },
    onError: (err) => toast.error(err.message),
  });

  const revokeMut = useMutation({
    mutationFn: ({ userId, variantName }: { userId: string; variantName: string }) =>
      badgesProvider.revokeFromUser(badge!.badge_id, userId, variantName),
    onSuccess: () => {
      toast.success("Badge revoked");
      setRevokeUserId("");
    },
    onError: (err) => toast.error(err.message),
  });

  const isDirty =
    badge &&
    (editName !== badge.name ||
      editActionType !== badge.action_type ||
      editActionThreshold !== String(badge.action_threshold) ||
      editActionDescription !== badge.action_description ||
      editIsVisible !== badge.is_visible ||
      editAvailableFrom !== toDatetimeLocal(badge.available_from) ||
      editAvailableTo !== toDatetimeLocal(badge.available_to));

  const canSave =
    editName.trim() &&
    editActionType.trim() &&
    editActionDescription.trim() &&
    editActionThreshold !== "" &&
    !isNaN(parseInt(editActionThreshold, 10));

  function buildUpdatePayload(): UpdateBadgePayload {
    return {
      name: editName.trim(),
      is_visible: editIsVisible,
      available_from: editAvailableFrom
        ? new Date(editAvailableFrom).toISOString()
        : undefined,
      available_to: editAvailableTo
        ? new Date(editAvailableTo).toISOString()
        : null,
      action_type: editActionType.trim(),
      action_threshold: parseInt(editActionThreshold, 10),
      action_description: editActionDescription.trim(),
    };
  }

  const anyPending =
    updateMut.isPending ||
    deleteMut.isPending ||
    uploadMut.isPending ||
    grantMut.isPending ||
    revokeMut.isPending;

  const variantOptions = variants ?? [];

  return (
    <Sheet open={!!badge} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="sm:max-w-md overflow-y-auto">
        {badge && (
          <>
            <SheetHeader>
              <SheetTitle>{badge.name}</SheetTitle>
              <SheetDescription className="font-mono text-xs">
                {badge.badge_id}
              </SheetDescription>
            </SheetHeader>

            {/* Image */}
            <div className="px-4 flex flex-col gap-2">
              {badge.img_url ? (
                <div
                  className="w-full rounded-lg border bg-muted overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => fileRef.current?.click()}
                >
                  <img
                    src={badge.img_url}
                    alt={badge.name}
                    className="w-full h-auto"
                  />
                </div>
              ) : (
                <div
                  className="w-full rounded-lg border bg-muted flex items-center justify-center py-10 cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => fileRef.current?.click()}
                >
                  <IconPhoto className="size-10 text-muted-foreground" />
                </div>
              )}
              <div className="flex flex-col gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploadMut.isPending}
                  className="gap-1.5"
                >
                  {uploadMut.isPending ? (
                    <IconLoader2 className="size-3.5 animate-spin" />
                  ) : (
                    <IconUpload className="size-3.5" />
                  )}
                  {badge.img_url ? "Replace image" : "Upload image"}
                </Button>
                <p className="text-xs text-muted-foreground">
                  JPEG or PNG, max 500 KB
                </p>
              </div>

              {/* Filter preview */}
              {badge.img_url && variantsWithFilter.length > 0 && (
                <div className="flex flex-col gap-2">
                  <Label className="text-xs">Preview with filter</Label>
                  <Select value={previewVariantName} onValueChange={setPreviewVariantName}>
                    <SelectTrigger className="text-xs h-8">
                      <SelectValue placeholder="Select a variant…" />
                    </SelectTrigger>
                    <SelectContent>
                      {variantsWithFilter.map((v) => (
                        <SelectItem key={v.variant_id} value={v.variant_name} className="text-xs font-mono">
                          {v.variant_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {previewVariantName && previewVariantName !== "standard" && (
                    <div className="flex flex-col gap-1.5 mt-1">
                      <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold">
                        Visual Effects
                      </Label>
                      <Select value={previewEffect} onValueChange={setPreviewEffect}>
                        <SelectTrigger className="text-xs h-8">
                          <div className="flex items-center gap-2">
                            <IconSparkles className="size-3.5 text-muted-foreground" />
                            <SelectValue placeholder="No effect" />
                          </div>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none" className="text-xs">None</SelectItem>
                          <SelectItem value="sparkles" className="text-xs">
                            <div className="flex items-center gap-2">
                              <IconSparkles className="size-3.5 text-yellow-500" />
                              <span>Sparkles</span>
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {previewVariantName && filterUrlByName[previewVariantName] && (
                    <>
                      <div className="flex items-center gap-2">
                        <input
                          type="range"
                          min={0}
                          max={100}
                          value={previewOpacity}
                          onChange={(e) => setPreviewOpacity(Number(e.target.value))}
                          className="flex-1 h-1.5 accent-primary"
                        />
                        <span className="text-xs text-muted-foreground w-8 text-right tabular-nums">
                          {previewOpacity}%
                        </span>
                      </div>
                      <BadgeFilterPreviewCanvas
                        baseImageUrl={badge.img_url}
                        filterImageUrl={filterUrlByName[previewVariantName]!}
                        opacity={previewOpacity / 100}
                        effect={previewEffect}
                      />
                    </>
                  )}
                </div>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  handleImageFile(
                    file,
                    (base64) => uploadMut.mutate(base64),
                    (msg) => toast.error(msg),
                  );
                  e.target.value = "";
                }}
              />
            </div>

            <Separator />

            {/* Edit metadata */}
            <div className="px-4 flex flex-col gap-4">
              <h3 className="font-semibold text-sm">Details</h3>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="edit-badge-name">Name</Label>
                <Input
                  id="edit-badge-name"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="edit-badge-action-type">Action type</Label>
                <Input
                  id="edit-badge-action-type"
                  value={editActionType}
                  onChange={(e) => setEditActionType(e.target.value)}
                  className="font-mono text-sm"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="edit-badge-threshold">Action threshold</Label>
                <Input
                  id="edit-badge-threshold"
                  type="number"
                  min={0}
                  value={editActionThreshold}
                  onChange={(e) => setEditActionThreshold(e.target.value)}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="edit-badge-action-desc">
                  Action description
                </Label>
                <Textarea
                  id="edit-badge-action-desc"
                  value={editActionDescription}
                  onChange={(e) => setEditActionDescription(e.target.value)}
                  rows={2}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="edit-badge-from">Available from</Label>
                <Input
                  id="edit-badge-from"
                  type="datetime-local"
                  value={editAvailableFrom}
                  onChange={(e) => setEditAvailableFrom(e.target.value)}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="edit-badge-to">Available to (optional)</Label>
                <Input
                  id="edit-badge-to"
                  type="datetime-local"
                  value={editAvailableTo}
                  onChange={(e) => setEditAvailableTo(e.target.value)}
                />
                {editAvailableTo && (
                  <button
                    type="button"
                    className="text-xs text-muted-foreground hover:text-destructive text-left"
                    onClick={() => setEditAvailableTo("")}
                  >
                    Clear (no expiry)
                  </button>
                )}
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="edit-badge-visible">Visible to users</Label>
                <Switch
                  id="edit-badge-visible"
                  checked={editIsVisible}
                  onCheckedChange={setEditIsVisible}
                />
              </div>

              {isDirty && (
                <Button
                  size="sm"
                  onClick={() => updateMut.mutate(buildUpdatePayload())}
                  disabled={updateMut.isPending || !canSave}
                  className="self-end"
                >
                  {updateMut.isPending ? (
                    <IconLoader2 className="size-4 animate-spin" />
                  ) : null}
                  Save changes
                </Button>
              )}
            </div>

            <Separator />

            {/* Variants */}
            <BadgeVariantsSection badge={badge} />

            <Separator />

            {/* Grant */}
            <div className="px-4 flex flex-col gap-3">
              <h3 className="font-semibold text-sm">Grant to user</h3>
              <div className="flex gap-2">
                <Input
                  placeholder="User ID"
                  value={grantUserId}
                  onChange={(e) => setGrantUserId(e.target.value)}
                  className="font-mono text-xs"
                />
                <Select
                  value={grantVariantName}
                  onValueChange={setGrantVariantName}
                  disabled={grantMut.isPending}
                >
                  <SelectTrigger className="w-28 text-xs shrink-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {variantOptions.length > 0 ? (
                      variantOptions.map((v: BadgeVariant) => (
                        <SelectItem key={v.variant_id} value={v.variant_name} className="text-xs font-mono">
                          {v.variant_name}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="standard" className="text-xs font-mono">
                        standard
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={grantMut.isPending || !grantUserId.trim()}
                  onClick={() =>
                    grantMut.mutate({
                      userId: grantUserId.trim(),
                      variantName: grantVariantName,
                    })
                  }
                  className="shrink-0 gap-1.5"
                >
                  {grantMut.isPending ? (
                    <IconLoader2 className="size-3.5 animate-spin" />
                  ) : (
                    <IconUserPlus className="size-3.5" />
                  )}
                  Grant
                </Button>
              </div>
            </div>

            <Separator />

            {/* Revoke */}
            <div className="px-4 flex flex-col gap-3">
              <h3 className="font-semibold text-sm">Revoke from user</h3>
              <div className="flex gap-2">
                <Input
                  placeholder="User ID"
                  value={revokeUserId}
                  onChange={(e) => setRevokeUserId(e.target.value)}
                  className="font-mono text-xs"
                />
                <Select
                  value={revokeVariantName}
                  onValueChange={setRevokeVariantName}
                  disabled={revokeMut.isPending}
                >
                  <SelectTrigger className="w-28 text-xs shrink-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {variantOptions.length > 0 ? (
                      variantOptions.map((v: BadgeVariant) => (
                        <SelectItem key={v.variant_id} value={v.variant_name} className="text-xs font-mono">
                          {v.variant_name}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="standard" className="text-xs font-mono">
                        standard
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={revokeMut.isPending || !revokeUserId.trim()}
                  onClick={() =>
                    revokeMut.mutate({
                      userId: revokeUserId.trim(),
                      variantName: revokeVariantName,
                    })
                  }
                  className="shrink-0 gap-1.5"
                >
                  {revokeMut.isPending ? (
                    <IconLoader2 className="size-3.5 animate-spin" />
                  ) : (
                    <IconUserMinus className="size-3.5" />
                  )}
                  Revoke
                </Button>
              </div>
            </div>

            <Separator />

            {/* Delete */}
            <div className="px-4 flex flex-col gap-3">
              <h3 className="font-semibold text-sm text-destructive">
                Danger zone
              </h3>
              {confirmDelete ? (
                <div className="flex flex-col gap-3 p-4 rounded-lg border border-destructive bg-destructive/5">
                  <div className="flex items-center gap-3 text-destructive">
                    <IconAlertTriangle className="size-5 shrink-0" />
                    <h4 className="font-bold text-sm uppercase tracking-wider">
                      Are you sure? This action is destructive.
                    </h4>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    This will permanently delete the badge and remove it from
                    all users. This cannot be undone.
                  </p>
                  <div className="flex gap-2 pt-1">
                    <Button
                      variant="destructive"
                      size="sm"
                      className="flex-1"
                      disabled={deleteMut.isPending}
                      onClick={() => deleteMut.mutate()}
                    >
                      {deleteMut.isPending ? (
                        <IconLoader2 className="size-3.5 animate-spin" />
                      ) : (
                        "Yes, delete badge"
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      disabled={deleteMut.isPending}
                      onClick={() => setConfirmDelete(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive border-destructive/30 hover:bg-destructive/10 self-start gap-1.5"
                  onClick={() => setConfirmDelete(true)}
                  disabled={anyPending}
                >
                  <IconTrash className="size-3.5" />
                  Delete badge
                </Button>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ── Variant types tab ─────────────────────────────────────────────────────────

function VariantTypeImageUpload({
  variantName,
  hasFilter,
}: {
  variantName: string;
  hasFilter: boolean;
}) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);

  const uploadMut = useMutation({
    mutationFn: (base64: string) =>
      badgesProvider.uploadVariantTypeImage(variantName, base64),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["badge-variant-types"] });
      toast.success("Filter image updated");
    },
    onError: (err) => toast.error(err.message),
  });

  const removeMut = useMutation({
    mutationFn: () => badgesProvider.removeVariantTypeImage(variantName),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["badge-variant-types"] });
      toast.success("Filter image removed");
    },
    onError: (err) => toast.error(err.message),
  });

  const busy = uploadMut.isPending || removeMut.isPending;

  return (
    <div className="flex items-center gap-1.5 justify-end">
      {hasFilter && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 gap-1.5 text-xs text-muted-foreground hover:text-destructive"
          disabled={busy}
          onClick={() => removeMut.mutate()}
        >
          {removeMut.isPending ? (
            <IconLoader2 className="size-3 animate-spin" />
          ) : (
            <IconTrash className="size-3" />
          )}
          Remove
        </Button>
      )}
      <Button
        variant="outline"
        size="sm"
        className="h-7 gap-1.5 text-xs"
        disabled={busy}
        onClick={() => fileRef.current?.click()}
      >
        {uploadMut.isPending ? (
          <IconLoader2 className="size-3 animate-spin" />
        ) : (
          <IconUpload className="size-3" />
        )}
        {uploadMut.isPending ? "Uploading…" : hasFilter ? "Replace" : "Upload image"}
      </Button>
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          handleImageFile(
            file,
            (base64) => uploadMut.mutate(base64),
            (msg) => toast.error(msg),
          );
          e.target.value = "";
        }}
      />
    </div>
  );
}

function VariantTypesTab() {
  const qc = useQueryClient();
  const createFileRef = useRef<HTMLInputElement>(null);

  const [newName, setNewName] = useState("");
  const [newFilterFile, setNewFilterFile] = useState<{
    file: File;
    base64: string;
  } | null>(null);

  const { data: variantTypes, isLoading, isError } = useQuery({
    queryKey: ["badge-variant-types"],
    queryFn: () => badgesProvider.listVariantTypes(),
    staleTime: 5 * 60_000,
  });

  const createMut = useMutation({
    mutationFn: () =>
      badgesProvider.createVariantType(newName.trim(), newFilterFile?.base64),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["badge-variant-types"] });
      toast.success("Variant type created");
      setNewName("");
      setNewFilterFile(null);
    },
    onError: (err) => toast.error(err.message),
  });

  const canCreate = newName.trim().length > 0;

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        Global dictionary of variant names available across all badges. A badge
        can only be given a variant that exists here.
      </p>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Filter overlay</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-48" /></TableCell>
                  <TableCell />
                </TableRow>
              ))
            ) : isError ? (
              <TableRow>
                <TableCell colSpan={3} className="py-10 text-center text-muted-foreground">
                  Failed to load variant types.
                </TableCell>
              </TableRow>
            ) : !variantTypes?.length ? (
              <TableRow>
                <TableCell colSpan={3} className="py-10 text-center text-muted-foreground">
                  No variant types found.
                </TableCell>
              </TableRow>
            ) : (
              variantTypes.map((vt) => (
                <TableRow key={vt.name}>
                  <TableCell>
                    <Badge
                      variant={vt.name === "standard" ? "secondary" : "outline"}
                      className="font-mono text-xs"
                    >
                      {vt.name}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {vt.filter_img_url ? (
                      <div className="flex items-center gap-2">
                        <img
                          src={vt.filter_img_url}
                          alt={vt.name}
                          className="size-6 rounded border object-cover"
                        />
                        <span className="text-xs text-muted-foreground font-mono truncate max-w-72">
                          {vt.filter_img_url}
                        </span>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">None</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <VariantTypeImageUpload variantName={vt.name} hasFilter={!!vt.filter_img_url} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create new variant type */}
      <div className="rounded-lg border p-4 flex flex-col gap-3">
        <h3 className="font-semibold text-sm">New variant type</h3>
        <div className="flex gap-2 items-end">
          <div className="flex flex-col gap-1.5 flex-1">
            <Label htmlFor="new-vt-name" className="text-xs">Name</Label>
            <Input
              id="new-vt-name"
              placeholder="e.g. gold"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="font-mono text-sm h-8"
              disabled={createMut.isPending}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs">Filter image (optional)</Label>
            <div className="flex items-center gap-2">
              <div
                className="size-8 rounded border bg-muted flex items-center justify-center overflow-hidden cursor-pointer hover:opacity-80 transition-opacity shrink-0"
                onClick={() => createFileRef.current?.click()}
              >
                {newFilterFile ? (
                  <img
                    src={URL.createObjectURL(newFilterFile.file)}
                    alt="preview"
                    className="size-full object-cover"
                  />
                ) : (
                  <IconPhoto className="size-4 text-muted-foreground" />
                )}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 text-xs"
                onClick={() => createFileRef.current?.click()}
                disabled={createMut.isPending}
              >
                <IconUpload className="size-3" />
                {newFilterFile ? "Change" : "Choose"}
              </Button>
              {newFilterFile && (
                <button
                  type="button"
                  className="text-xs text-muted-foreground hover:text-destructive"
                  onClick={() => setNewFilterFile(null)}
                >
                  <IconX className="size-3.5" />
                </button>
              )}
            </div>
          </div>
          <Button
            size="sm"
            className="h-8 gap-1.5"
            disabled={!canCreate || createMut.isPending}
            onClick={() => createMut.mutate()}
          >
            {createMut.isPending ? (
              <IconLoader2 className="size-3.5 animate-spin" />
            ) : (
              <IconPlus className="size-3.5" />
            )}
            Create
          </Button>
        </div>
        <input
          ref={createFileRef}
          type="file"
          accept="image/jpeg,image/png"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            handleImageFile(
              file,
              (base64) => setNewFilterFile({ file, base64 }),
              (msg) => toast.error(msg),
            );
            e.target.value = "";
          }}
        />
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function BadgesPage() {
  const [page, setPage] = useState(1);
  const [selectedBadge, setSelectedBadge] = useState<BadgeDto | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const {
    data: badges,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["badges"],
    queryFn: () => badgesProvider.listAll(),
    staleTime: 60_000,
  });

  const paginated =
    badges?.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE) ?? [];
  const hasMore = (badges?.length ?? 0) > page * PAGE_SIZE;

  return (
    <div className="flex flex-col h-full">
      <SiteHeader title="Badges" />

      <div className="flex flex-col gap-4 flex-1 p-4 lg:p-6">
        <Tabs defaultValue="badges">
          <TabsList>
            <TabsTrigger value="badges">Badges</TabsTrigger>
            <TabsTrigger value="variant-types">Variant Types</TabsTrigger>
          </TabsList>

          <TabsContent value="badges" className="flex flex-col gap-4 mt-4">
            {/* Toolbar */}
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-muted-foreground">
                <IconMedal className="size-4" />
                <span className="text-sm">
                  {isLoading
                    ? "Loading…"
                    : badges
                      ? `${badges.length} badge${badges.length !== 1 ? "s" : ""}`
                      : ""}
                </span>
              </div>
              <Button
                size="sm"
                onClick={() => setCreateOpen(true)}
                className="gap-1.5"
              >
                <IconPlus className="size-4" />
                New badge
              </Button>
            </div>

            {/* Table */}
            <div className="rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Badge ID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Variants</TableHead>
                    <TableHead>Action type</TableHead>
                    <TableHead>Threshold</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Visibility</TableHead>
                    <TableHead>Availability</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={i}>
                        {Array.from({ length: 8 }).map((_, j) => (
                          <TableCell key={j}>
                            <Skeleton className="h-4 w-full" />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : isError ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        className="py-12 text-center text-muted-foreground"
                      >
                        Failed to load badges. Check your connection and try again.
                      </TableCell>
                    </TableRow>
                  ) : !paginated.length ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        className="py-12 text-center text-muted-foreground"
                      >
                        No badges yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginated.map((badge) => (
                      <BadgeRow
                        key={badge.badge_id}
                        badge={badge}
                        onClick={() => setSelectedBadge(badge)}
                      />
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {(page > 1 || hasMore) && (
              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="gap-1"
                >
                  <IconChevronLeft className="size-3.5" />
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">Page {page}</span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!hasMore}
                  onClick={() => setPage((p) => p + 1)}
                  className="gap-1"
                >
                  Next
                  <IconChevronRight className="size-3.5" />
                </Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="variant-types" className="mt-4">
            <VariantTypesTab />
          </TabsContent>
        </Tabs>
      </div>

      <CreateBadgeSheet
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
      <BadgeDetailSheet
        badge={selectedBadge}
        onClose={() => setSelectedBadge(null)}
      />
    </div>
  );
}
